import React, { useState, useEffect } from 'react';
import {
  Card,
  CardContent,
  CardHeader,
  Box,
  Typography,
  Button,
  CircularProgress,
  Alert,
  IconButton,
  TextField,
  InputAdornment,
  Tooltip,
  Chip,
  Divider
} from '@mui/material';
import {
  Share as ShareIcon,
  ContentCopy as CopyIcon,
  Add as AddIcon,
  Settings as SettingsIcon,
  CheckCircle as CheckCircleIcon
} from '@mui/icons-material';
import { generateInvitationLink, getInvitationTokens } from '../../services/teacherService';
import { TeacherInvitationToken } from '../../types/teacher';
import InvitationManagementDialog from './InvitationManagementDialog';

interface InvitationLinkCardProps {
  refreshTrigger?: number;
}

const InvitationLinkCard: React.FC<InvitationLinkCardProps> = ({ refreshTrigger }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeToken, setActiveToken] = useState<TeacherInvitationToken | null>(null);
  const [invitationUrl, setInvitationUrl] = useState<string>('');
  const [copied, setCopied] = useState(false);
  const [managementDialogOpen, setManagementDialogOpen] = useState(false);
  const [generating, setGenerating] = useState(false);

  useEffect(() => {
    fetchActiveToken();
  }, [refreshTrigger]);

  const fetchActiveToken = async () => {
    setLoading(true);
    setError(null);
    try {
      const tokens = await getInvitationTokens();

      // Find the most recent active token
      const active = tokens.find(t => t.isActive && new Date(t.expiresAt) > new Date());

      if (active) {
        setActiveToken(active);
        const url = `${window.location.origin}/register?invite=${active.token}`;
        setInvitationUrl(url);
      } else {
        setActiveToken(null);
        setInvitationUrl('');
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to load invitation tokens';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleGenerateLink = async () => {
    setGenerating(true);
    setError(null);
    try {
      const newToken = await generateInvitationLink();
      setActiveToken(newToken);
      const url = `${window.location.origin}/register?invite=${newToken.token}`;
      setInvitationUrl(url);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to generate invitation link';
      setError(errorMessage);
    } finally {
      setGenerating(false);
    }
  };

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(invitationUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy to clipboard:', err);
    }
  };

  const handleManagementDialogClose = () => {
    setManagementDialogOpen(false);
    fetchActiveToken(); // Refresh after management dialog closes
  };

  const formatExpirationDate = (date: Date | string) => {
    const expirationDate = new Date(date);
    const now = new Date();
    const diffMs = expirationDate.getTime() - now.getTime();
    const diffDays = Math.ceil(diffMs / (1000 * 60 * 60 * 24));

    if (diffDays < 0) {
      return 'Expired';
    } else if (diffDays === 0) {
      return 'Expires today';
    } else if (diffDays === 1) {
      return 'Expires tomorrow';
    } else {
      return `Expires in ${diffDays} days`;
    }
  };

  return (
    <>
      <Card elevation={2}>
        <CardHeader
          avatar={<ShareIcon color="primary" />}
          title="Student Invitation Link"
          action={
            activeToken && (
              <Tooltip title="Manage Invitations">
                <IconButton onClick={() => setManagementDialogOpen(true)}>
                  <SettingsIcon />
                </IconButton>
              </Tooltip>
            )
          }
        />
        <CardContent>
          {loading ? (
            <Box display="flex" justifyContent="center" alignItems="center" py={3}>
              <CircularProgress />
            </Box>
          ) : error ? (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          ) : null}

          {!loading && !activeToken && (
            <Box textAlign="center" py={2}>
              <Typography variant="body1" color="text.secondary" gutterBottom>
                Create a shareable invitation link for your students
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Students who register using this link will be automatically added to your class
              </Typography>
              <Button
                variant="contained"
                startIcon={generating ? <CircularProgress size={20} /> : <AddIcon />}
                onClick={handleGenerateLink}
                disabled={generating}
              >
                {generating ? 'Generating...' : 'Generate Invitation Link'}
              </Button>
            </Box>
          )}

          {!loading && activeToken && (
            <Box>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Share this link with students to let them register under your class:
              </Typography>

              <TextField
                fullWidth
                value={invitationUrl}
                variant="outlined"
                size="small"
                sx={{ my: 2 }}
                InputProps={{
                  readOnly: true,
                  endAdornment: (
                    <InputAdornment position="end">
                      <Tooltip title={copied ? 'Copied!' : 'Copy to clipboard'}>
                        <IconButton onClick={handleCopyLink} edge="end">
                          {copied ? <CheckCircleIcon color="success" /> : <CopyIcon />}
                        </IconButton>
                      </Tooltip>
                    </InputAdornment>
                  )
                }}
              />

              <Divider sx={{ my: 2 }} />

              <Box display="flex" justifyContent="space-between" alignItems="center">
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    {formatExpirationDate(activeToken.expiresAt)}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Used {activeToken.timesUsed} time{activeToken.timesUsed !== 1 ? 's' : ''}
                    {activeToken.lastUsedAt && (
                      <> • Last used {new Date(activeToken.lastUsedAt).toLocaleDateString()}</>
                    )}
                  </Typography>
                </Box>
                <Chip
                  label="Active"
                  color="success"
                  size="small"
                  variant="outlined"
                />
              </Box>

              <Box mt={2} display="flex" gap={1}>
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<SettingsIcon />}
                  onClick={() => setManagementDialogOpen(true)}
                >
                  Manage
                </Button>
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<AddIcon />}
                  onClick={handleGenerateLink}
                  disabled={generating}
                >
                  Generate New
                </Button>
              </Box>
            </Box>
          )}
        </CardContent>
      </Card>

      <InvitationManagementDialog
        open={managementDialogOpen}
        onClose={handleManagementDialogClose}
      />
    </>
  );
};

export default InvitationLinkCard;
